import React, { useEffect, useRef } from 'react';
import { LocateFixed } from 'lucide-react';
import { Language, Spot, UserLocation } from '../types/spot';
import L from 'leaflet';
import { resolveSpotImage, getCategoryPlaceholder, getImmediateSpotImageUrl } from '../services/imageResolver';
import { getSpotMapLinks } from '../services/mapLinks';
import { formatCompactTransitBadge, calculateDistance } from '../services/geolocation';

interface MapViewProps {
  spots: Spot[];
  userLocation: UserLocation;
  radiusKm: number;
  onSelectSpot: (spot: Spot) => void;
  language: Language;
  isLocationReady: boolean;
  onRequestGps: () => void;
}

export const MapView: React.FC<MapViewProps> = ({
  spots,
  userLocation,
  radiusKm,
  onSelectSpot,
  language,
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersLayerRef = useRef<L.LayerGroup | null>(null);
  const userMarkerRef = useRef<L.LayerGroup | null>(null);
  const prevUserLocRef = useRef<{ lat: number; lng: number }>({
    lat: userLocation.lat,
    lng: userLocation.lng,
  });

  // Initialize Leaflet Map with keyless CartoDB Dark Matter tiles
  useEffect(() => {
    if (!mapContainerRef.current || mapInstanceRef.current) return;

    const initialZoom = radiusKm >= 100 ? 10 : radiusKm >= 25 ? 11 : 13;
    const map = L.map(mapContainerRef.current, {
      center: [userLocation.lat, userLocation.lng],
      zoom: initialZoom,
      zoomControl: true,
    });

    // OpenStreetMap Tiles with Cyber Dark CSS filter (no watermarks, keyless, maxZoom 19)
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors',
      maxZoom: 19,
      className: 'cyber-map-tiles',
    }).addTo(map);

    markersLayerRef.current = L.layerGroup().addTo(map);
    userMarkerRef.current = L.layerGroup().addTo(map);
    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  // Update Center & User Marker
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const locChanged =
      Math.abs(prevUserLocRef.current.lat - userLocation.lat) > 0.0001 ||
      Math.abs(prevUserLocRef.current.lng - userLocation.lng) > 0.0001;

    if (locChanged) {
      prevUserLocRef.current = { lat: userLocation.lat, lng: userLocation.lng };
      const targetZoom = radiusKm >= 100 ? 10 : radiusKm >= 25 ? 11 : 13;
      map.setView([userLocation.lat, userLocation.lng], targetZoom, { animate: true });
    }

    if (userMarkerRef.current) {
      userMarkerRef.current.clearLayers();

      // Electric Cyan GPS pin with radar pulse
      const userIcon = L.divIcon({
        className: 'user-gps-pin',
        html: `
          <div style="
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: #00f0ff;
            border: 2px solid #090d16;
            box-shadow: 0 0 12px rgba(0, 240, 255, 0.8), 0 0 24px rgba(0, 240, 255, 0.4);
            position: relative;
          ">
            <div style="
              position: absolute;
              inset: -6px;
              border: 2px solid #00f0ff;
              border-radius: 50%;
              opacity: 0.6;
              animation: dot-pulse 2s infinite ease-out;
            "></div>
          </div>
        `,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      });

      const marker = L.marker([userLocation.lat, userLocation.lng], { icon: userIcon });
      userMarkerRef.current.addLayer(marker);

      // Search Radius Circle
      if (radiusKm > 0 && radiusKm <= 150) {
        const radiusCircle = L.circle([userLocation.lat, userLocation.lng], {
          radius: radiusKm * 1000,
          color: '#00f0ff',
          fillColor: '#00f0ff',
          fillOpacity: 0.06,
          weight: 1.5,
          dashArray: '4, 6',
        });
        userMarkerRef.current.addLayer(radiusCircle);
      }
    }
  }, [userLocation, radiusKm]);

  // Update Spots Markers
  useEffect(() => {
    const map = mapInstanceRef.current;
    const layer = markersLayerRef.current;
    if (!map || !layer) return;

    layer.clearLayers();

    spots.forEach((spot) => {
      const title = language === 'de' ? spot.nameDe : language === 'kr' ? spot.nameKr : spot.name;
      
      let pinColor = '#475569';
      let glowColor = 'rgba(71, 85, 105, 0.4)';
      let isFeatured = false;

      if (spot.isMustSee || spot.isEvent || spot.category === 'event_festival') {
        pinColor = '#00f0ff';
        glowColor = 'rgba(0, 240, 255, 0.7)';
        isFeatured = true;
      } else if (spot.category === 'halal_food' || spot.category === 'vegetarian_food') {
        pinColor = '#10b981';
        glowColor = 'rgba(16, 185, 129, 0.5)';
      } else if (spot.category === 'shopping') {
        pinColor = '#c084fc';
        glowColor = 'rgba(192, 132, 252, 0.5)';
      } else if (spot.category === 'palace_heritage') {
        pinColor = '#fbbf24';
        glowColor = 'rgba(251, 191, 36, 0.5)';
      } else if (spot.category === 'activity') {
        pinColor = '#38bdf8';
        glowColor = 'rgba(56, 189, 248, 0.5)';
      } else if (spot.category === 'fitness') {
        pinColor = '#f97316';
        glowColor = 'rgba(249, 115, 22, 0.6)';
        isFeatured = spot.isMustSee;
      }

      const customIcon = L.divIcon({
        className: 'custom-spot-marker',
        html: `
          <div style="
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background: ${pinColor};
            border: 2px solid #090d16;
            box-shadow: 0 0 10px ${glowColor}, 0 2px 6px rgba(0, 0, 0, 0.6);
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: transform 0.15s ease;
          ">
            <div style="width: 6px; height: 6px; border-radius: 50%; background: ${isFeatured ? '#090d16' : '#ffffff'};"></div>
          </div>
        `,
        iconSize: [24, 24],
        iconAnchor: [12, 12],
      });

      const marker = L.marker([spot.lat, spot.lng], { icon: customIcon });

      // Edler Dark Cyber Card Popup
      const cachedImage = getImmediateSpotImageUrl(spot);
      const links = getSpotMapLinks(spot, userLocation);
      const detailsLabel = language === 'kr' ? '상세 정보 보기' : language === 'en' ? 'View Details' : 'Details öffnen';

      const popupHtml = `
        <div style="width: 240px; font-family: -apple-system, BlinkMacSystemFont, 'Pretendard', sans-serif; background: #0f1523; border: 1px solid rgba(0, 240, 255, 0.25); border-radius: 12px; overflow: hidden; box-shadow: 0 8px 30px rgba(0,0,0,0.7), 0 0 16px rgba(0, 240, 255, 0.15); color: #f8fafc;">
          <img id="popup-img-${spot.id}" src="${cachedImage}" alt="${title}" onerror="this.src='${getCategoryPlaceholder(spot.category)}'" style="width: 100%; height: 115px; object-fit: cover; cursor: pointer; border-bottom: 1px solid rgba(255,255,255,0.06);" />
          <div style="padding: 12px; background: #0f1523;">
            <div style="display: flex; justify-content: space-between; align-items: baseline; gap: 4px;">
              <span style="font-size: 11px; font-weight: 700; color: #00f0ff;">${spot.nameKr}</span>
              ${spot.calculatedDistance !== undefined ? `<span style="font-size: 11px; font-weight: 700; color: #94a3b8; background: #161e31; padding: 2px 6px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.08); flex-shrink: 0;">${formatCompactTransitBadge(spot.calculatedDistance, language)}</span>` : ''}
            </div>
            <div id="popup-title-${spot.id}" style="font-size: 13.5px; font-weight: 700; color: #f8fafc; margin-top: 3px; cursor: pointer; line-height: 1.3;">${title}</div>
            <div style="font-size: 11px; color: #94a3b8; margin-top: 4px;">${spot.subwayInfo.split('(')[0]}</div>
            <button id="popup-detail-btn-${spot.id}" type="button" style="width: 100%; margin-top: 10px; padding: 8px 10px; border-radius: 8px; border: none; background: #00f0ff; color: #090d16; font-size: 12px; font-weight: 700; cursor: pointer; text-align: center; box-shadow: 0 0 12px rgba(0, 240, 255, 0.35);">${detailsLabel}</button>
            <div style="display: flex; gap: 5px; margin-top: 8px;">
              <a href="${links.naverWeb}" target="_blank" rel="noopener noreferrer" style="flex: 1; background: #03C75A; color: #fff; text-align: center; padding: 6px 2px; border-radius: 6px; font-size: 11px; font-weight: 700; text-decoration: none;">Naver</a>
              <a href="${links.kakaoWeb}" target="_blank" rel="noopener noreferrer" style="flex: 1; background: #FEE500; color: #191919; text-align: center; padding: 6px 2px; border-radius: 6px; font-size: 11px; font-weight: 700; text-decoration: none;">Kakao</a>
              <a href="${links.googleWeb}" target="_blank" rel="noopener noreferrer" style="flex: 1; background: #161e31; color: #cbd5e1; border: 1px solid rgba(255,255,255,0.1); text-align: center; padding: 6px 2px; border-radius: 6px; font-size: 11px; font-weight: 700; text-decoration: none;">Google</a>
            </div>
          </div>
        </div>
      `;

      marker.bindPopup(popupHtml, { maxWidth: 250 });
      marker.on('popupopen', () => {
        const detailBtn = document.getElementById(`popup-detail-btn-${spot.id}`);
        const imgEl = document.getElementById(`popup-img-${spot.id}`) as HTMLImageElement | null;
        const titleEl = document.getElementById(`popup-title-${spot.id}`);

        const openDetail = () => onSelectSpot(spot);
        if (detailBtn) detailBtn.onclick = openDetail;
        if (imgEl) imgEl.onclick = openDetail;
        if (titleEl) titleEl.onclick = openDetail;

        resolveSpotImage(spot).then((url) => {
          const currentImgEl = document.getElementById(`popup-img-${spot.id}`) as HTMLImageElement | null;
          if (currentImgEl && url) {
            currentImgEl.src = url;
          }
        });
      });
      layer.addLayer(marker);
    });

    // If the displayed spots are all far from the current map center (e.g. user selected Jeju/Busan/Daegu tab), fit bounds to spots
    if (spots.length > 0) {
      const center = map.getCenter();
      const closestSpotKm = spots.reduce((minKm, s) => {
        const d = calculateDistance(center.lat, center.lng, s.lat, s.lng);
        return d < minKm ? d : minKm;
      }, Infinity);

      if (closestSpotKm > 30) {
        const bounds = L.latLngBounds(spots.map((s) => [s.lat, s.lng] as [number, number]));
        map.fitBounds(bounds, { padding: [36, 36], maxZoom: 13, animate: true });
      }
    }
  }, [spots, language, userLocation, onSelectSpot]);

  const handleCenterOnUser = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView([userLocation.lat, userLocation.lng], 14, { animate: true });
    }
  };

  const spotsOnMapLabel = language === 'kr'
    ? `지도에 ${spots.length}개 장소 표시`
    : language === 'en'
    ? `${spots.length} spots on map`
    : `${spots.length} Spots auf der Karte`;

  const centerTooltip = language === 'kr'
    ? '내 위치로 이동'
    : language === 'en'
    ? 'Center on my location'
    : 'Auf meinen Standort zentrieren';

  return (
    <div className="map-container">
      <div ref={mapContainerRef} className="w-full h-full" />

      {/* Floating Top Left: Spots Count */}
      <div className="map-floating map-floating--tl">
        <span className="map-dot" />
        <span>{spotsOnMapLabel}</span>
      </div>

      {/* Floating Center Button */}
      <div style={{ position: 'absolute', bottom: 24, right: 16, zIndex: 400 }}>
        <button
          onClick={handleCenterOnUser}
          className="map-center-btn"
          title={centerTooltip}
        >
          <LocateFixed style={{ width: 20, height: 20 }} />
        </button>
      </div>
    </div>
  );
};
